/**
 * PR Drawer Header — sheet title with currency pill + sub-description line.
 * Used by `PRDetailDrawer` only.
 */
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtAmt, fmtDate } from "@/lib/flags";
import { Building2, User, Target } from "lucide-react";

export default function DrawerHeader({ pr, loading }) {
  // Prefer human-friendly plant name from COMP master; fall back to raw code.
  const plantLabel = pr?.plant_name || pr?.plant;
  const endCustLabel = pr?.end_cust_name || pr?.end_cust;
  return (
    <SheetHeader className="bg-cyan-700 text-white px-6 py-5">
      <SheetTitle className="text-white text-xl flex items-center gap-2">
        {loading ? "Loading PR..." : pr ? (
          <>
            PR #{pr.voc_no}
            {pr.currency && pr.currency !== "INR" && (
              <span
                data-testid="pr-currency-pill"
                className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold bg-amber-300 text-amber-900 border border-amber-400 leading-none"
                title={`Entered in ${pr.currency} · Exch rate ${fmtAmt(pr.exch_rate)}`}
              >
                {pr.currency} @ {fmtAmt(pr.exch_rate)}
              </span>
            )}
          </>
        ) : "PR detail"}
      </SheetTitle>
      {pr ? (
        <>
          <SheetDescription className="text-sm text-cyan-50 mt-1">
            {pr.purc_type} · {fmtDate(pr.vdate)} · {pr.supplier}
          </SheetDescription>
          {/* Plant + Requestor pills — compact, wraps naturally on narrow
              screens. Uses cyan tint so it stays visually part of the header. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-cyan-50" data-testid="pr-drawer-meta">
            {plantLabel && (
              <span className="inline-flex items-center gap-1" data-testid="pr-drawer-plant" title={pr.plant ? `Compcode: ${pr.plant}` : ""}>
                <Building2 size={11} className="text-cyan-200" />
                <span className="font-semibold text-white truncate max-w-[220px]">{plantLabel}</span>
                {pr.plant && pr.plant_name && pr.plant !== plantLabel && (
                  <span className="text-cyan-200/80">({pr.plant})</span>
                )}
              </span>
            )}
            {pr.requestor_name && (
              <span className="inline-flex items-center gap-1" data-testid="pr-drawer-requestor">
                <User size={11} className="text-cyan-200" />
                <span className="text-cyan-100">Requestor:</span>
                <span className="font-semibold text-white truncate max-w-[220px]">{pr.requestor_name}</span>
              </span>
            )}
            {endCustLabel && (
              <span className="inline-flex items-center gap-1" data-testid="pr-drawer-end-cust"
                    title={pr.end_cust ? `EndCust code: ${pr.end_cust}` : ""}>
                <Target size={11} className="text-cyan-200" />
                <span className="text-cyan-100">End customer:</span>
                <span className="font-semibold text-white truncate max-w-[220px]">{endCustLabel}</span>
                {pr.end_cust && pr.end_cust_name && pr.end_cust !== endCustLabel && (
                  <span className="text-cyan-200/80">({pr.end_cust})</span>
                )}
              </span>
            )}
          </div>
        </>
      ) : (
        <SheetDescription className="text-cyan-50 text-sm">Purchase requisition details</SheetDescription>
      )}
    </SheetHeader>
  );
}
